import { useCallback, useMemo, useState } from "react";
import { useSelector } from "@tanstack/react-store";
import type { BadgeProps } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import {
  localStorageConfigItems,
  localStorageConfigStore,
  type LocalStorageConfigState,
} from "../../stores/localStorageConfigStore";
import type { DevToolsPanelProps } from "./DevToolsPanel";
import { getObjectKeys } from "../../object";

export const useDevToolsPanelController = ({ environment }: DevToolsPanelProps) => {
  const i18n = useI18n();
  const localStorageConfig = useSelector(localStorageConfigStore, (state) => state);
  const [localStorageConfigState, setLocalStorageConfigState] = useState<LocalStorageConfigState>(localStorageConfig);

  const setLocalStorageConfigStateWithValidation = useCallback((newState: LocalStorageConfigState) => {
    setLocalStorageConfigState((prevState) => {
      const targetState = { ...prevState, ...newState };
      if (!targetState.psmConfigEnableMSW && targetState.psmConfigOverrideUserRole) {
        targetState.psmConfigOverrideUserRole = "";
      }
      return targetState;
    });
  }, []);

  const envBadgeVariant: BadgeProps["variant"] = useMemo(() => {
    switch (environment.env) {
      case "PROD":
        return "brand-primary";
      case "DQS":
        return "accent";
      case "DEV":
        return "constructive";
      default:
        return "outline";
    }
  }, [environment]);

  const appVersion = useMemo(() => environment?.git?.commit.hash, [environment]);

  const appVersionUrl = useMemo(
    () => appVersion && `https://gitlab.com/booking-com/flights/main/-/commit/${appVersion}`,
    [appVersion]
  );

  const shouldReload = useCallback(() => {
    const changedKeys = getObjectKeys(localStorageConfigState).filter(
      (key) => localStorageConfig[key] !== localStorageConfigState[key]
    );

    return localStorageConfigItems
      .filter(({ localStorageKey }) => changedKeys.includes(localStorageKey))
      .some(({ reloadOnChange }) => reloadOnChange);
  }, [localStorageConfig, localStorageConfigState]);


  return {
    environment,
    envBadgeVariant,
    appVersion,
    appVersionUrl,
    language: i18n.language,
    localStorageConfigState,
    setLocalStorageConfigState: setLocalStorageConfigStateWithValidation,
    shouldReload,
  };
};
